import _ from 'lodash';
import { catalogOptions } from '@/builder/catalog';
import {
  ALL_CONFIG_SECTION_PRODUCT_IDS,
  CONFIG_SECTION_PRODUCT_IDS,
  HINGED_PRODUCT_ID,
  KITCHEN_LAUNDRY_PRODUCT_IDS,
  PRODUCT,
} from '@/builder/products';

export { catalogOptions };

// Pure option-filter helpers for the builder store.
// Getters in stores/builder.js stay as the public API and apply any store side effects.

const UNBOUNDED_MAX = 9999999999999;

export const NO_HANDLES_OPTION = {
  id: 0,
  title: 'No Handles required, I’ll source my own',
  description: null,
  image: null,
  gallery: null,
  subGroup: '',
  subGroupSeq: 0
};

function optionMinBound(opt, key) {
  const value = parseFloat(opt[key]);
  return value > 0 ? value : 0;
}

function optionMaxBound(opt, key) {
  const value = parseFloat(opt[key]);
  return value > 0 ? value : UNBOUNDED_MAX;
}

export function optionFitsWidth(opt, width) {
  const value = parseFloat(width);
  return value >= optionMinBound(opt, 'minWidth') && value <= optionMaxBound(opt, 'maxWidth');
}

function optionFitsHeightExclusiveMin(opt, height) {
  const compareHeight = parseFloat(height);
  return compareHeight > optionMinBound(opt, 'minHeight') && compareHeight <= optionMaxBound(opt, 'maxHeight');
}

function optionRequiredIds(opt) {
  const raw = opt.requiredId;
  if (raw == null || raw === '') return [];
  return Array.isArray(raw) ? raw : [raw];
}

function optionMatchesRequiredId(opt, requiredId) {
  const ids = optionRequiredIds(opt);
  if (!ids.length || requiredId == null || requiredId === '') return false;
  return ids.some((id) => id == requiredId);
}

function includesLoose(list, value) {
  if (!list || !list.length) return false;
  return list.some((item) => item == value);
}

function parseLayoutSectionIds(raw) {
  if (!raw && raw !== 0) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== 'string') return [raw];
  try {
    const parsed = JSON.parse(raw.replaceAll('"', ''));
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch (e) {
    return [raw];
  }
}

export function optionMatchesLayoutSection(opt, sectionId) {
  const raw = opt.productLayoutSectionIds;
  if (!raw && raw !== 0) return false;

  // Preserve the original string check: any value containing "all" matches every section
  if (typeof raw === 'string' && raw.search('all') !== -1) return true;

  const sectionIds = parseLayoutSectionIds(raw);
  if (includesLoose(sectionIds, 'all')) return true;
  return includesLoose(sectionIds, sectionId);
}

export function withSubGroupTitles(opts) {
  const ordered = _.orderBy(opts, ['subGroupSeq'], ['asc']);
  const subGroups = [];
  ordered.forEach((item) => {
    const displaySubGroupTitle = !subGroups.includes(item.subGroup);
    if (displaySubGroupTitle) subGroups.push(item.subGroup);
    item.displaySubGroupTitle = displaySubGroupTitle;
  });
  return ordered;
}

export function filterProductLayoutOptions(productLayouts, productId) {
  if (_.isEmpty(productLayouts) || !productLayouts.options) return [];
  return productLayouts.options.filter((el) => el.productId == productId);
}

export function filterWalkInWardrobeLayoutOptions(layoutOptions) {
  if (_.isUndefined(layoutOptions)) return [];
  return layoutOptions.filter((opt) => opt.productId !== HINGED_PRODUCT_ID);
}

export function filterHingedWardrobeLayoutOptions(layoutOptions, doorsMin, doorsMax) {
  if (_.isUndefined(layoutOptions)) return [];
  return layoutOptions.filter((opt) => {
    const doors = parseFloat(opt.slug.split('-')[1]);
    return doors >= parseFloat(doorsMin) && doors <= parseFloat(doorsMax);
  });
}

export function filterInteriorOptions(interiorOptions, doorsId, width) {
  if (_.isUndefined(interiorOptions)) return [];
  return interiorOptions.filter((opt) => {
    if (!optionMatchesRequiredId(opt, doorsId)) return false;
    const value = parseFloat(width);
    if (value < optionMinBound(opt, 'minWidth')) return false;
    // maxWidth on these options is the old 2/3/4-door width band.
    return true;
  });
}

export function filterInteriorOptionsForSection({
  interiorOptions,
  productLayoutSections,
  sectionIndex,
  sectionWidth,
  productId,
  configOptions
}) {
  if (_.isUndefined(interiorOptions)) return [];
  if (_.isUndefined(productLayoutSections) || _.isNull(productLayoutSections) || !_.isArray(productLayoutSections)) {
    return [];
  }

  const isHinged = !_.isUndefined(configOptions) && productId == HINGED_PRODUCT_ID;
  const layoutSection = productLayoutSections[sectionIndex];
  const doorsId = layoutSection.doorsId ?? null;
  const currentSectionId = isHinged ? doorsId : layoutSection.id;

  return interiorOptions.filter((opt) => {
    if (!optionMatchesLayoutSection(opt, currentSectionId)) return false;
    if (!isHinged && !optionFitsWidth(opt, sectionWidth)) return false;
    return true;
  });
}

export function filterPanelsOptions(panelsOptions, width) {
  if (_.isUndefined(panelsOptions)) return [];
  return panelsOptions.filter((opt) => optionFitsWidth(opt, width));
}

export function filterEndPanelFinishOptions(framesOptions, framesId) {
  const filteredOpts = [
    {
      id: null,
      title: 'Standard (White Melamine)'
    }
  ];
  if (_.isUndefined(framesOptions)) return filteredOpts;
  framesOptions.forEach((opt) => {
    if (opt.hasEndPanelFinish && opt.id == framesId) {
      filteredOpts.push(opt);
    }
  });
  return filteredOpts;
}

export function filterTopsOptions(topsOptions, height) {
  if (_.isUndefined(topsOptions)) return [];
  return topsOptions.filter((opt) => optionFitsHeightExclusiveMin(opt, height));
}

export function filterHingedDoorsOptions(hingedDoorsOptions, doorsMin, doorsMax) {
  if (_.isUndefined(hingedDoorsOptions)) return [];
  return hingedDoorsOptions.filter((opt) => {
    const doors = parseFloat(opt.doors);
    return doors >= parseFloat(doorsMin) && doors <= parseFloat(doorsMax);
  });
}

export function filterDeliveryOptions(deliveryOptions, isDeliveryOnly) {
  if (_.isUndefined(deliveryOptions)) return [];
  const options = [...deliveryOptions];
  if (isDeliveryOnly) {
    return options.filter((opt) => opt.id !== 'pickup');
  }
  return options;
}

export function filterDoorProfileOptions(doorProfileOptions) {
  return withSubGroupTitles(doorProfileOptions);
}

export function filterPanelFinishOptions(panelFinishOptions, doorProfileId) {
  return withSubGroupTitles(panelFinishOptions).filter((item) => {
    return _.isEmpty(item.requiredModIds) || item.requiredModIds.includes(doorProfileId);
  });
}

export function filterBenchtopMaterialOptions(benchtopMaterialOptions) {
  return withSubGroupTitles(benchtopMaterialOptions);
}

export function filterHandlesOptions(handlesOptions) {
  if (_.isUndefined(handlesOptions)) return [];
  return [...handlesOptions, NO_HANDLES_OPTION];
}

export function filterSubConfigOptions(allOpts, configGroup, availableWidth, hasLayoutSections) {
  if (_.isUndefined(allOpts) || !hasLayoutSections) return [];

  return allOpts.filter((opt) => {
    let isValid = false;
    if (configGroup && configGroup.configIds && configGroup.configIds.indexOf(opt.id) !== -1) {
      isValid = true;
    }
    if (opt.type !== 'sub') isValid = false;
    if (isValid && availableWidth < optionMinBound(opt, 'minWidth')) isValid = false;
    if (isValid && availableWidth > optionMaxBound(opt, 'maxWidth')) isValid = false;
    return isValid;
  });
}

export function applySubConfigOverrides(configOptions, filteredOpts) {
  if (!configOptions || !filteredOpts.length) return;
  const index = configOptions.map((e) => e.id).indexOf(filteredOpts[0].configParentId);
  if (index < 0) return;
  if (filteredOpts.length == 1) {
    configOptions[index].overrideTitle = filteredOpts[0].title;
    configOptions[index].overrideImage = filteredOpts[0].image;
    configOptions[index].overrideGallery = filteredOpts[0].gallery;
  } else {
    configOptions[index].overrideTitle = null;
    configOptions[index].overrideImage = null;
    configOptions[index].overrideGallery = null;
  }
}

export function filterConfigOptionsForSection({
  configOptions,
  productId,
  productLayoutSections,
  sectionIndex,
  sectionWidth,
  itemWidth,
  availableWidth,
  structureMinWidth,
  configOptionsMinWidth,
  doorsId,
  features,
  featuresFilter
}) {
  const empty = {
    filteredOpts: [],
    validOpts: [],
    featuresAvailable: null,
    didMatchProduct: false,
    didRun: false,
    didFilterByWidth: false,
    allOpts: configOptions
  };

  if (!includesLoose(CONFIG_SECTION_PRODUCT_IDS, productId)) return empty;
  empty.didMatchProduct = true;
  if (_.isUndefined(configOptions) || _.isUndefined(productLayoutSections) || _.isNull(productLayoutSections)) {
    return empty;
  }

  const featuresAvailable = {};
  for (let j = 0; j < features.length; j++) {
    featuresAvailable[features[j].id] = 0;
  }

  const validOpts = [];
  const filteredOpts = [];

  let width = null;
  if (!_.isNil(sectionWidth)) width = sectionWidth;
  else if (!_.isNil(itemWidth)) width = itemWidth;

  let minWidth = structureMinWidth;
  if (!_.isNull(configOptionsMinWidth)) minWidth = configOptionsMinWidth;

  const didFilterByWidth = !!(width && width >= minWidth);

  if (didFilterByWidth && productLayoutSections[sectionIndex]) {
    const currentSectionId = productLayoutSections[sectionIndex].id;

    for (let i = 0; i < configOptions.length; i++) {
      const opt = configOptions[i];
      let isValid = true;
      let isFiltered = true;

      if (!optionMatchesLayoutSection(opt, currentSectionId)) isValid = false;
      if (opt.type !== 'standard') isValid = false;

      if (isValid && availableWidth < optionMinBound(opt, 'minWidth')) isValid = false;

      const matchesDoor = optionMatchesRequiredId(opt, doorsId);
      if (isValid && optionRequiredIds(opt).length && !matchesDoor) isValid = false;

      // Sliding 2-door configs still carry the old 2300mm maxWidth band.
      // Door count is height-aware now; keep minWidth, drop that max cap when the door mod matches.
      if (isValid && availableWidth > optionMaxBound(opt, 'maxWidth')) {
        if (!(productId == PRODUCT.SLIDING2 && matchesDoor)) isValid = false;
      }

      if (isValid) {
        const featuresRequired = featuresFilter.length;
        let featuresCount = 0;
        const featuresMatched = [];
        const optFeatures = opt.features || [];
        for (let j = 0; j < optFeatures.length; j++) {
          const featureId = optFeatures[j];
          if (featuresFilter.indexOf(featureId) !== -1 && featuresMatched.indexOf(featureId) == -1) {
            featuresMatched.push(featureId);
            featuresCount++;
          }
          if (!featuresFilter.length) {
            featuresAvailable[featureId]++;
          }
        }
        if (featuresFilter.length && featuresCount >= featuresRequired) {
          for (let j = 0; j < optFeatures.length; j++) {
            const featureId = optFeatures[j];
            featuresAvailable[featureId]++;
          }
        }
        if (featuresFilter.length && featuresCount < featuresRequired) isFiltered = false;
      }

      if (isValid) validOpts.push(opt);
      if (isValid && isFiltered) filteredOpts.push(opt);
    }
  }

  return {
    filteredOpts,
    validOpts,
    featuresAvailable,
    didMatchProduct: true,
    didRun: true,
    didFilterByWidth,
    allOpts: configOptions
  };
}

export function filterAllConfigOptionsForSection({
  configOptions,
  productId,
  productLayoutSections,
  sectionIndex
}) {
  if (_.isUndefined(configOptions)) return [];
  if (!includesLoose(ALL_CONFIG_SECTION_PRODUCT_IDS, productId)) return [];
  if (_.isUndefined(productLayoutSections) || _.isNull(productLayoutSections) || _.isUndefined(productLayoutSections[sectionIndex])) return [];

  const currentSectionId = productLayoutSections[sectionIndex].id;
  return configOptions.filter((opt) => optionMatchesLayoutSection(opt, currentSectionId));
}

export function configOptionsMinWidthForItem(allOpts, productId) {
  if (!includesLoose(KITCHEN_LAUNDRY_PRODUCT_IDS, productId)) return null;
  let minWidth = null;
  for (let i = 0; i < allOpts.length; i++) {
    if (allOpts[i]) {
      if (_.isNull(minWidth) || (allOpts[i].minWidth > 0 && allOpts[i].minWidth < minWidth)) {
        minWidth = allOpts[i].minWidth;
      }
    }
  }
  return minWidth;
}

export function configOptionsMaxWidthForItem(allOpts, productId) {
  if (!includesLoose(ALL_CONFIG_SECTION_PRODUCT_IDS, productId)) return null;
  let maxWidth = null;
  for (let i = 0; i < allOpts.length; i++) {
    if (allOpts[i]) {
      if (_.isNull(maxWidth) || allOpts[i].maxWidth > maxWidth) {
        maxWidth = allOpts[i].maxWidth;
      }
    }
  }
  return maxWidth;
}
